import { message } from 'antd';
import FetchUtils, { parseJSON } from './fetch_utils';
import { getBackendUri } from '../utils/EnvConfigUtils';
import { ROLE_TYPES } from '../utils/DataConstants';
import { logoutSuccess } from '../actions/session_actions';
import { WORK_ORDERS_CRUD_ACTION_CREATORS } from '../actions/work_orders_actions';
import { currentUserEmail, WorkOrderUserStar } from '../data/workOrderStarLabels';
import { compareStrings, nullSafeGet, nullSafeGetOrElse } from '../utils/DataAccessUtils';

const fetchUtils = new FetchUtils();

// Personal bookmark (star) on a work order with an optional short note.
//   PUT    /api/v1/{role}/work_order/work_orders/:woId/star   { starNote? }
//   DELETE /api/v1/{role}/work_order/work_orders/:woId/star
const starUrl = (roleType: string, workOrderId: number | string): string =>
	`${getBackendUri()}/api/v1/${roleType}/work_order/work_orders/${workOrderId}/star`;

export const applyWorkOrderStar =
	(workOrderId: number | string, starred: boolean, note: string | null) => (dispatch, getState) => {
		const email = currentUserEmail();
		const state = getState();
		const searchStars = nullSafeGet(`work_orders.searchRecords.${workOrderId}.stars`, state);
		const existing: WorkOrderUserStar[] =
			searchStars != null
				? searchStars
				: nullSafeGetOrElse(`work_orders.records.${workOrderId}.stars`, state, []);
		// Case-insensitive match (see getWorkOrderStarState): casing differences between the
		// cached email and star.email must not leave a stale duplicate of the user's own star.
		const withoutMine = existing.filter((star) => star && !compareStrings(star.email, email));
		const nextStars = starred
			? [...withoutMine, { email, workOrderId, starNote: note || null }]
			: withoutMine;
		dispatch(
			WORK_ORDERS_CRUD_ACTION_CREATORS.updatePartialSuccess({ id: workOrderId, stars: nextStars })
		);
	};

const upsertWorkOrderStarForRole =
	(roleType: string) => (workOrderId: number | string, starNote?: string | null) => {
		return (dispatch) =>
			new Promise((resolve, reject) => {
				fetchUtils
					.put(starUrl(roleType, workOrderId), {
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
						data: { starNote: starNote ? starNote : null },
					})
					.then(parseJSON)
					.then((result) => {
						const status = result.status;
						const ok = result.ok;
						const json = result.json;
						if (status === 401) {
							dispatch(logoutSuccess());
						}
						if (!ok || (json && json.status === 'error')) {
							message.error('Failed to update star.');
							reject(json && json.message ? json.message : 'Failed to update star.');
						} else {
							dispatch(applyWorkOrderStar(workOrderId, true, starNote ? starNote : null));
							resolve(json && json.data !== undefined ? json.data : json);
						}
					})
					.catch((error) => {
						message.error('Failed to update star.');
						reject(error && error.message);
					});
			});
	};

const removeWorkOrderStarForRole = (roleType: string) => (workOrderId: number | string) => {
	return (dispatch) =>
		new Promise((resolve, reject) => {
			fetchUtils
				.delete(starUrl(roleType, workOrderId), {
					headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
				})
				.then(parseJSON)
				.then((result) => {
					const status = result.status;
					const ok = result.ok;
					const json = result.json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (!ok || (json && json.status === 'error')) {
						message.error('Failed to remove star.');
						reject(json && json.message ? json.message : 'Failed to remove star.');
					} else {
						dispatch(applyWorkOrderStar(workOrderId, false, null));
						resolve(json && json.data !== undefined ? json.data : json);
					}
				})
				.catch((error) => {
					message.error('Failed to remove star.');
					reject(error && error.message);
				});
		});
};

export const upsertWorkOrderStarForBuyer = upsertWorkOrderStarForRole(ROLE_TYPES.BUYER);
export const upsertWorkOrderStarForSupplier = upsertWorkOrderStarForRole(ROLE_TYPES.SUPPLIER);
export const removeWorkOrderStarForBuyer = removeWorkOrderStarForRole(ROLE_TYPES.BUYER);
export const removeWorkOrderStarForSupplier = removeWorkOrderStarForRole(ROLE_TYPES.SUPPLIER);

export const upsertWorkOrderStar = (userType) =>
	userType === ROLE_TYPES.SUPPLIER ? upsertWorkOrderStarForSupplier : upsertWorkOrderStarForBuyer;
export const removeWorkOrderStar = (userType) =>
	userType === ROLE_TYPES.SUPPLIER ? removeWorkOrderStarForSupplier : removeWorkOrderStarForBuyer;
